import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { sendToApi } from 'api/client'
import { UseMutationOptions } from 'react-query/types/react/types'
import { Data } from 'hooks/queries/workspaces/useCreateBrandVisualKit'
import { BrandKit } from 'types'

interface Props {
  id: number
}

export type Options = Omit<UseMutationOptions<Data>, 'mutationKey' | 'mutationFn'>

export const deleteBrandKit = (id: number, workspaceId: number) =>
  sendToApi(`/w/${workspaceId}/brand_kits/${id}`, { method: 'DELETE' })

export const useDeleteBrandKit = ({ id }: Props, options?: Options) => {
  const queryClient = useQueryClient()
  const currentWorkspaceId = useCurrentWorkspaceId()
  return useMutation(
    ['workspaces', currentWorkspaceId, 'brandKits', id],
    async () => {
      const response = await deleteBrandKit(id, currentWorkspaceId)
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }
      return response.data
    },
    {
      ...options,
      onMutate: async () => {
        queryClient.setQueryData(
          ['workspaces', currentWorkspaceId, 'brandKits', 'list'],
          (old: BrandKit[] | undefined) => {
            if (!old) return []
            return old.filter((oldBrandKit: BrandKit) => oldBrandKit.id !== id)
          },
        )
      },
      onError: (_errors, _variables, context) => {
        queryClient.setQueryData(['workspaces', currentWorkspaceId, 'brandKits', 'list'], context)
      },
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'list'])
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
